import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import { Unicode11Addon } from '@xterm/addon-unicode11';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { WebglAddon } from '@xterm/addon-webgl';
import { Terminal } from '@xterm/xterm';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton, XStack, YStack } from '@/components/ui';
import { logError } from '@/lib/errorLogging';
import { useTheme } from '@/providers/ThemeProvider';
import { isMac } from '@/utils/capabilities';

import {
  encodeKillMessage,
  encodeResizeMessage,
  parseAuthAckFrame,
  parseExitFrame,
  parseTitleFrame,
  type AuthAck,
} from './sandbox-terminal-protocol';
import { terminalSettingsStore } from './terminal-settings.store';
import { readTerminalTheme } from './terminal-theme';
import { TerminalFindBar } from './TerminalFindBar';

import '@xterm/xterm/css/xterm.css';

/** How long the container must hold a size before the grid follows it. */
const RESIZE_SETTLE_MS = 80;
/** Longest the grid stays hidden after being shown, waiting for xterm's first repaint. */
const REVEAL_CAP_MS = 120;

/** cmd+f on mac; ctrl+shift+f elsewhere, since plain ctrl+f is readline forward-char. */
function isFindShortcut(event: KeyboardEvent): boolean {
  if (event.key.toLowerCase() !== 'f') return false;
  return isMac()
    ? event.metaKey && !event.ctrlKey && !event.altKey
    : event.ctrlKey && event.shiftKey && !event.altKey;
}

export interface TerminalConnection {
  wsUrl: string;
  /** First text frame after open; the server authenticates on it. */
  authFrame: string;
  /**
   * True when `authFrame` carries the grid and the server spawns (or already
   * runs) the PTY at that size, so no resize is needed after auth. Omit for
   * servers whose PTY starts at a default size; the view then sends one.
   */
  authFrameCarriesGrid?: boolean;
}

interface TerminalViewProps {
  /** Used in logs and as the header when `header` is not given. */
  title: string;
  /**
   * Replaces the default title in the header row (e.g. a tab strip). The find bar
   * and close button are always rendered after it.
   */
  header?: ReactNode;
  /**
   * When given, the header's right-most button calls this instead of closing this
   * view and renders as a down chevron instead of an X. The local terminal
   * dock passes a collapse handler so the button collapses the pane;
   * the per-tab X (in the tab strip) still kills a single tab.
   */
  onDockClose?: () => void;
  /**
   * Keyboard shortcuts owned by the embedding surface rather than the shell, checked
   * on keydown while xterm has focus. Return true to consume the event (xterm never
   * sees it). Must be referentially stable.
   */
  onKeyCommand?: (event: KeyboardEvent) => boolean;
  /** Local host pushed a new window title for this session. Must be referentially stable. */
  onTitle?: (title: string) => void;
  /**
   * Where to connect, resolved after xterm has fit its grid so the server can spawn
   * the PTY at the right size. Must be referentially stable (useCallback): a new
   * identity tears the session down and reconnects.
   */
  resolveConnection: (grid: {
    cols: number;
    rows: number;
  }) => Promise<TerminalConnection>;
  /** Log copy for a non-1000 close code. */
  describeClose: (code: number) => string;
  /**
   * Called when the user closes the panel and when the session ends (shell exit,
   * socket drop, failed connect). The default embedding removes the tab; a run tab
   * instead keeps the view so the output stays readable (see `exitBanner`).
   */
  onClose: () => void;
  /**
   * Print a dim status line into the terminal when the server reports the process
   * exited. For tabs that stay open after exit; pointless when onClose unmounts.
   */
  exitBanner?: boolean;
  /**
   * Server accepted the auth frame. Local host includes the session id so the
   * caller can reattach to the same shell later. Must be referentially stable.
   */
  onAuthed?: (ack: AuthAck) => void;
  /**
   * If true the close button also tells the server to end the session (local host:
   * SIGHUP the shell). Otherwise close just drops the socket and the server decides
   * (sandbox: PTY dies with the socket; local: session lingers for reattach).
   */
  killOnClose?: boolean;
  /**
   * Called once per session with a function that focuses the terminal; returns an
   * unregister function. Lets a hotkey focus the shell without prop churn. Must be
   * referentially stable, like `resolveConnection`.
   */
  registerFocus?: (focus: () => void) => () => void;
  /**
   * Same shape, for a function that ends the session (sends `kill`; the server
   * answers with exit + close, which routes to onClose). Lets a tab strip or hotkey
   * close a terminal that isn't the one rendering the button.
   */
  /** The kill returns true if it reached the host; false means the socket was
   *  already gone and the caller must kill the session another way. */
  registerKill?: (kill: () => boolean) => () => void;
}

/**
 * xterm.js bound to a websocket speaking the terminal wire protocol
 * (sandbox-terminal-protocol.ts): keystrokes out as binary, PTY output in as
 * binary, resizes as JSON control frames. Backend-agnostic - the sandbox and
 * local terminals are thin wrappers that only differ in `resolveConnection`.
 *
 * Fills its parent; mount only while the terminal should be live - the socket
 * closes on unmount. One mount = one session: parents `key` this component when
 * the connection identity changes rather than mutating props on a live session.
 */
export const TerminalView = observer(function TerminalView({
  title,
  header,
  onDockClose,
  onKeyCommand,
  onTitle,
  resolveConnection,
  describeClose,
  onClose,
  onAuthed,
  exitBanner = false,
  killOnClose = false,
  registerFocus,
  registerKill,
}: TerminalViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const searchRef = useRef<SearchAddon | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  // Latest onClose without making it an effect dep (parents pass inline lambdas).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [findOpen, setFindOpen] = useState(false);

  // Reads only refs, so one instance is valid for the component's whole life and
  // can be handed to registerKill from inside the connection effect.
  const killRef = useRef((): boolean => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      // the server replies with exit + close(1000), which routes to onClose
      ws.send(encodeKillMessage());
      return true;
    }
    onCloseRef.current();
    return false;
  });
  const handleCloseClick = () =>
    onDockClose ? onDockClose() : killOnClose ? killRef.current() : onClose();

  const { theme: mode } = useTheme();
  const { resolvedFontFamily: fontFamily, fontSize } = terminalSettingsStore;
  // Latest appearance for the connection effect to seed a new Terminal with. Read
  // through a ref so appearance is not a dep of that effect: a font or theme change
  // must restyle in place (effects below), never reconnect. And any reconnect - a
  // changed callback identity, HMR - must still come up styled, so it cannot rely
  // on those effects having run.
  const appearanceRef = useRef({ mode, fontFamily, fontSize });
  appearanceRef.current = { mode, fontFamily, fontSize };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let ws: WebSocket | null = null;
    const encoder = new TextEncoder();

    const appearance = appearanceRef.current;
    const initialTheme = readTerminalTheme(appearance.mode);
    if (initialTheme.background) {
      container.style.backgroundColor = initialTheme.background;
    }
    const term = new Terminal({
      cursorBlink: true,
      scrollback: 5000,
      // unicode11 + search decorations are behind xterm's proposed-API flag
      allowProposedApi: true,
      fontFamily: appearance.fontFamily,
      fontSize: appearance.fontSize,
      theme: initialTheme,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    fitRef.current = fit;
    const search = new SearchAddon();
    term.loadAddon(search);
    searchRef.current = search;
    // Find and the embedder's commands (tabs) are ours, not the shell's.
    // Everything else passes through to the PTY. stopPropagation matters: the
    // app's own cmd+t / cmd+w (chat tabs) listen on document and would fire too.
    term.attachCustomKeyEventHandler((event) => {
      if (event.type !== 'keydown') return true;
      if (isFindShortcut(event)) {
        event.preventDefault();
        event.stopPropagation();
        setFindOpen(true);
        return false;
      }
      if (onKeyCommand?.(event)) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
      return true;
    });
    // Unicode 11 widths so emoji/CJK/Nerd Font glyphs in prompts don't misalign
    term.loadAddon(new Unicode11Addon());
    term.unicode.activeVersion = '11';
    term.loadAddon(
      new WebLinksAddon((event, uri) => {
        event.preventDefault();
        window.open(uri, '_blank', 'noopener');
      })
    );
    term.open(container);
    // GPU renderer; the DOM renderer stalls on bursty output. Falls back to DOM
    // on context loss, which also covers machines without WebGL2.
    let webgl: WebglAddon | null = null;
    try {
      webgl = new WebglAddon();
      webgl.onContextLoss(() => {
        webgl?.dispose();
        webgl = null;
      });
      term.loadAddon(webgl);
    } catch {
      webgl = null;
    }
    fit.fit();
    termRef.current = term;
    const unregisterFocus = registerFocus?.(() => term.focus());
    const unregisterKill = registerKill?.(killRef.current);

    // Every resize the server applies becomes a SIGWINCH, and zsh answers a
    // SIGWINCH by redrawing its prompt - printing its inverse-video `%` if the
    // cursor was not at column 0. Only send sizes the PTY does not already have.
    let serverCols = 0;
    let serverRows = 0;
    const sendResize = (cols: number, rows: number) => {
      if (cols === serverCols && rows === serverRows) return;
      if (ws?.readyState !== WebSocket.OPEN) return;
      serverCols = cols;
      serverRows = rows;
      ws.send(encodeResizeMessage(cols, rows));
    };

    const connect = async () => {
      const { wsUrl, authFrame, authFrameCarriesGrid } =
        await resolveConnection({ cols: term.cols, rows: term.rows });
      if (disposed) return;
      // Only the local host spawns the PTY at the grid in the auth frame; the
      // sandbox auth frame carries no size, so its PTY is at the server
      // default until we resize it. Leave the tracked size at zero for that
      // case so the post-auth sendResize is not deduplicated away.
      if (authFrameCarriesGrid) {
        serverCols = term.cols;
        serverRows = term.rows;
      }

      const socket = new WebSocket(wsUrl);
      ws = socket;
      wsRef.current = socket;
      socket.binaryType = 'arraybuffer';
      socket.onopen = () => socket.send(authFrame);
      socket.onmessage = (event: MessageEvent<string | ArrayBuffer>) => {
        if (typeof event.data === 'string') {
          const ack = parseAuthAckFrame(event.data);
          if (ack) {
            onAuthed?.(ack);
            if (ack.title !== undefined) onTitle?.(ack.title);
            // The host reports the grid it actually applied (the sandbox does not,
            // so fall back to what we asked for). Then sync to wherever xterm
            // settled while connecting - a no-op if nothing moved.
            if (ack.cols !== undefined && ack.rows !== undefined) {
              serverCols = ack.cols;
              serverRows = ack.rows;
            }
            sendResize(term.cols, term.rows);
            // A login shell can take a second to come up; if the user has moved
            // on to the chat composer meanwhile, don't pull them back.
            const active = document.activeElement;
            if (
              !active ||
              active === document.body ||
              container.contains(active)
            ) {
              term.focus();
            }
            return;
          }
          const newTitle = parseTitleFrame(event.data);
          if (newTitle !== null) onTitle?.(newTitle);
          if (exitBanner) {
            const exit = parseExitFrame(event.data);
            if (exit) {
              const status =
                exit.code === null
                  ? 'terminated'
                  : `exited with code ${exit.code}`;
              term.write(`\r\n\x1b[2m[process ${status}]\x1b[0m\r\n`);
            }
          }
          // the close that follows routes to onClose
          return;
        }
        term.write(new Uint8Array(event.data));
      };
      socket.onclose = (event) => {
        if (disposed) return;
        if (event.code === 1000) {
          onCloseRef.current();
          return;
        }
        // Not the shell ending (that is a 1000 with an exit frame before it):
        // the host went away or refused us. Say so in the terminal and stay
        // mounted, so whatever was on screen is still readable and the user
        // closes the tab when they are done - killRef falls through to onClose
        // once the socket is gone.
        logError(
          new Error(describeClose(event.code)),
          'Terminal socket closed abnormally',
          { context: 'TerminalView', metadata: { title, code: event.code } }
        );
        showDisconnected(describeClose(event.code));
      };
    };

    const showDisconnected = (reason: string) => {
      term.write(
        `\r\n\x1b[2m[${reason}. Close this tab and open a new one to reconnect.]\x1b[0m\r\n`
      );
    };

    void connect().catch((error) => {
      logError(error, 'Terminal connect failed', {
        context: 'TerminalView',
        metadata: { title },
      });
      if (!disposed) showDisconnected('Could not connect to the terminal');
    });

    const dataSubscription = term.onData((data) => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(encoder.encode(data));
      }
    });
    // fit() updates the local grid; onResize then propagates it to the server PTY.
    const resizeSubscription = term.onResize(({ cols, rows }) =>
      sendResize(cols, rows)
    );
    // Skip zero-size observations: the dock is display:none while hidden and
    // fitting to that would collapse the grid (and the PTY) to nothing.
    //
    // Settle, don't track: while the dock edge is dragged the container
    // changes every frame, and each row-count crossing would reflow the grid
    // and SIGWINCH the shell, which clears and redraws its prompt - visible as
    // flashing text. Fit once the size has held still for a beat instead.
    // Going from hidden to shown fits immediately - nothing is on screen yet
    // to flash, and a beat of wrong grid on every open would be worse.
    //
    // Shown also gates paint: xterm pauses its renderer while the element is
    // display:none, so the canvas still holds the last frame drawn before the
    // dock closed - in whatever theme was current then. It repaints only after
    // its own IntersectionObserver fires, a frame or two after we are visible,
    // and that stale frame is the old appearance flashing on open. Hide the
    // grid until xterm reports a render (the container keeps painting its
    // already-updated background), with a short cap in case none comes.
    // Opacity, not visibility: the dock focuses the terminal in this same
    // window, and a visibility:hidden element refuses focus.
    let fitTimer: ReturnType<typeof setTimeout> | null = null;
    let revealTimer: ReturnType<typeof setTimeout> | null = null;
    let revealSubscription: { dispose(): void } | null = null;
    let wasHidden = true;
    const reveal = () => {
      if (revealTimer !== null) clearTimeout(revealTimer);
      revealTimer = null;
      revealSubscription?.dispose();
      revealSubscription = null;
      term.element?.style.removeProperty('opacity');
    };
    const resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (!(width > 0 && height > 0)) {
        wasHidden = true;
        return;
      }
      if (wasHidden) {
        wasHidden = false;
        fit.fit();
        if (term.element) {
          term.element.style.opacity = '0';
          revealSubscription = term.onRender(reveal);
          revealTimer = setTimeout(reveal, REVEAL_CAP_MS);
          // Nothing may be dirty (same theme, no output while hidden), in
          // which case xterm would never repaint and we would sit on the cap.
          // Ask for a full repaint: if xterm is still paused this is queued
          // for its unpause, otherwise it renders on the next frame.
          term.refresh(0, term.rows - 1);
        }
        return;
      }
      if (fitTimer !== null) clearTimeout(fitTimer);
      fitTimer = setTimeout(() => {
        fitTimer = null;
        if (!disposed) fit.fit();
      }, RESIZE_SETTLE_MS);
    });
    resizeObserver.observe(container);

    return () => {
      disposed = true;
      if (fitTimer !== null) clearTimeout(fitTimer);
      reveal();
      resizeObserver.disconnect();
      dataSubscription.dispose();
      resizeSubscription.dispose();
      unregisterFocus?.();
      unregisterKill?.();
      ws?.close();
      if (wsRef.current === ws) wsRef.current = null;
      webgl?.dispose();
      term.dispose();
      if (termRef.current === term) termRef.current = null;
      if (searchRef.current === search) searchRef.current = null;
      if (fitRef.current === fit) fitRef.current = null;
    };
  }, [
    resolveConnection,
    describeClose,
    onAuthed,
    onTitle,
    onKeyCommand,
    registerFocus,
    registerKill,
    title,
    exitBanner,
  ]);

  // Live updates for an existing Terminal. Construction already applied the
  // current values, so these only matter when a setting changes mid-session.

  // Font from settings; a change alters cell size, so refit the grid after.
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;
    term.options.fontFamily = fontFamily;
    term.options.fontSize = fontSize;
    // Same guard as the resize observer: a hidden tab / collapsed dock has a
    // zero-size container, and fitting to it would shrink the PTY to nothing.
    // The observer refits when the tab is shown again.
    const el = containerRef.current;
    if (el && el.clientWidth > 0 && el.clientHeight > 0) fitRef.current?.fit();
  }, [fontFamily, fontSize]);

  // Sync the running xterm instance and its container with the app theme without
  // reconnecting. Resolved from CSS tokens (scoped to `mode`, see terminal-theme.ts)
  // so it also tracks the glass/ambient variants.
  useEffect(() => {
    const theme = readTerminalTheme(mode);
    const term = termRef.current;
    if (term) term.options.theme = theme;
    const container = containerRef.current;
    if (container && theme.background) {
      container.style.backgroundColor = theme.background;
    }
  }, [mode]);

  return (
    <YStack className="h-full w-full min-h-0">
      <XStack className="shrink-0 items-center gap-2 border-b border-border px-3 py-1">
        {header ?? (
          <>
            <Icons.terminal className="size-3.5 shrink-0 text-foreground-subtle" />
            <span className="flex-1 truncate text-sm font-medium text-foreground">
              {title}
            </span>
          </>
        )}
        {findOpen && (
          <TerminalFindBar
            search={searchRef.current}
            onClose={() => {
              setFindOpen(false);
              searchRef.current?.clearDecorations();
              termRef.current?.focus();
            }}
          />
        )}
        <IconButton
          size="sm"
          icon={onDockClose ? Icons.chevronDown : Icons.cancel}
          tooltip={onDockClose ? 'Collapse terminal pane' : 'Close terminal'}
          aria-label={onDockClose ? 'Collapse terminal pane' : 'Close terminal'}
          onClick={handleCloseClick}
        />
      </XStack>
      <div
        ref={containerRef}
        className="min-h-0 w-full flex-1 overflow-hidden p-2"
      />
    </YStack>
  );
});
